/** A server stamp as an instant, reading a stamp with no zone as UTC.
 *
 * The server writes two shapes for the same clock. `datetime.now(timezone.utc)`
 * ends in `+00:00`; `datetime.utcnow()`, which every Pydantic `created_at`
 * default uses, ends in nothing. `new Date` reads the second as LOCAL time, so
 * an insight and a find saved at one moment showed ages an offset apart to every
 * customer outside UTC. Both shapes are UTC, so name the zone the server meant.
 */
export const parseServerDate = (
	isoDate: string | null | undefined,
): Date | null => {
	if (!isoDate) return null;
	// Only a date-TIME with no zone is rewritten. A date-only string is already
	// UTC by the spec, and `2026-09-04Z` is not a format the spec defines, so
	// appending there would rely on one engine's fallback parser.
	const needsZone =
		/T\d{2}:\d{2}/.test(isoDate) && !/(Z|[+-]\d{2}:?\d{2})$/.test(isoDate);
	const date = new Date(needsZone ? `${isoDate}Z` : isoDate);
	return Number.isNaN(date.getTime()) ? null : date;
};

export const formatRelativeTime = (
	isoDate: string | null | undefined,
): string => {
	const date = parseServerDate(isoDate);
	if (!date) return 'Unknown';
	const now = new Date();
	const diffMs = Math.max(0, now.getTime() - date.getTime());
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

	if (diffDays === 0) {
		const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
		if (diffHours === 0) {
			const diffMinutes = Math.floor(diffMs / (1000 * 60));
			if (diffMinutes < 1) return 'Just now';
			return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
		}
		return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
	}

	if (diffDays === 1) return '1 day ago';
	if (diffDays < 7) return `${diffDays} days ago`;

	const diffWeeks = Math.floor(diffDays / 7);
	if (diffWeeks === 1) return '1 week ago';
	if (diffWeeks < 4) return `${diffWeeks} weeks ago`;

	const diffMonths = Math.floor(diffDays / 30);
	if (diffMonths === 1) return '1 month ago';
	if (diffMonths < 12) return `${diffMonths} months ago`;

	const diffYears = Math.floor(diffDays / 365);
	return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
};
